/**
 * @module components/formulation/FeedEditor
 * @summary Which line a charging unit is on, and which of its materials it doses.
 * @description
 *   The same control in both views — the 2D properties panel and the 3D
 *   selection bar's popover — because "what does this feeder dose" is one
 *   question and two answers would drift apart.
 *
 *   Two steps, in this order. First the LINE: a feeder belongs to a production
 *   line, and that is what turns a percentage into kilograms. Then the
 *   MATERIAL, offered from that line's recipe and nothing else — which is the
 *   whole point of asking for the line first. Picking from 152 grades when the
 *   recipe names six is how a feeder ends up dosing something the line has
 *   never heard of.
 *
 *   No kilograms are typed here. The recipe and the line rate settle them.
 */
import { useEffect, useRef, useState } from 'react';
import { EP_INDEX } from '../../data/epMaterialIndex';
import { engine } from '../../engine/CanvasEngine';
import {
  allFormulations,
  formulationName,
  formulationOf,
  materialKgh,
  materialName,
  shareOf,
} from '../../engine/formulation';
import { formatRate } from '../../engine/balance';
import type { FeedItem, Shape } from '../../types';

/** Every part, grouped by category — only used on a drawing with no lines. */
const ALL_GROUPS: { category: string; parts: { part: string; name: string }[] }[] = (() => {
  const by = new Map<string, { part: string; name: string }[]>();
  for (const [part, e] of Object.entries(EP_INDEX)) {
    const list = by.get(e.category) ?? [];
    list.push({ part, name: e.name });
    by.set(e.category, list);
  }
  return [...by.entries()]
    .map(([category, parts]) => ({
      category,
      parts: parts.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
})();

interface Props {
  shape: Shape;
  /** Bumped by the caller when the document changes under us, to re-read. */
  version: number;
}

export function FeedEditor({ shape, version }: Props) {
  /* Rows are held here while they are being edited, not read back from the
     engine on every render: a row that has just been added names no material
     yet, and `setFeed` drops those, so a reader would delete the row before it
     could be filled in. */
  const [rows, setRows] = useState<FeedItem[]>(() => (shape.feed ?? []).map((r) => ({ ...r })));

  /* What the document held the last time this component and it agreed. The
     3D selection bar re-reads five times a second, and without somewhere to
     tell our own writes apart from somebody else's, every one of those ticks
     would overwrite the row being edited. */
  const echo = useRef(JSON.stringify(shape.feed ?? []));
  const shownFor = useRef(shape.id);

  useEffect(() => {
    const incoming = JSON.stringify(shape.feed ?? []);
    // Our own change coming back round: leave the editor alone.
    if (shownFor.current === shape.id && incoming === echo.current) return;
    // A different item, or an undo — adopt what the document now says.
    shownFor.current = shape.id;
    echo.current = incoming;
    setRows((shape.feed ?? []).map((r) => ({ ...r })));
  }, [shape.id, version, shape.feed]);

  const commit = (next: FeedItem[]) => {
    setRows(next);
    engine.setFeed(next);
    // `setFeed` drops rows naming no material, so what landed is not always
    // what we sent. Record what landed.
    echo.current = JSON.stringify(shape.feed ?? []);
  };
  const patch = (i: number, p: Partial<FeedItem>) =>
    commit(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));

  const lines = allFormulations();
  const f = formulationOf(shape);
  /** What this feeder may dose: its line's recipe, or anything when unbound. */
  const choices = f
    ? f.lines.map((l) => ({ part: l.part, name: materialName(l.part) }))
    : undefined;

  return (
    <div className="fe">
      {lines.length > 0 && (
        <div className="fe-line">
          <label htmlFor={'fe-line-' + shape.id}>Line</label>
          <select
            id={'fe-line-' + shape.id}
            value={shape.formulationId ?? ''}
            title="Which production line this item belongs to. Its recipe is what this feeder may dose, and its rate is what the dose is worked out from."
            onChange={(e) => engine.setFormulationOf(e.target.value)}
          >
            <option value="">— not on a line —</option>
            {lines.map((l) => (
              <option key={l.id} value={l.id}>
                {formulationName(l)}
              </option>
            ))}
          </select>
        </div>
      )}

      {lines.length > 0 && !f ? (
        <div className="fe-note">
          Put this item on a line first. Its recipe is what decides which materials it can dose, and
          how many kilograms of each.
        </div>
      ) : f && !f.lines.length ? (
        <div className="fe-note">
          {formulationName(f)} has no materials on its recipe yet. Add them in the Formulation tab
          and they will appear here.
        </div>
      ) : (
        <>
          {rows.map((r, i) => {
            const share = shareOf(r);
            const kgh = f ? (materialKgh(f, r.part) * share) / 100 : 0;
            const split = rows.length > 1 || share !== 100;
            return (
              <div className="fe-row" key={i}>
                <select
                  className="fe-sel"
                  value={r.part}
                  aria-label="Material"
                  onChange={(e) => patch(i, { part: e.target.value })}
                >
                  <option value="">— pick a material —</option>
                  {choices
                    ? choices.map((c) => (
                        <option key={c.part} value={c.part}>
                          {c.name}
                        </option>
                      ))
                    : ALL_GROUPS.map((g) => (
                        <optgroup key={g.category} label={g.category}>
                          {g.parts.map((p) => (
                            <option key={p.part} value={p.part}>
                              {p.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                </select>
                {f ? (
                  <input
                    className="fe-kgh"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    placeholder="%"
                    title="How much of this material's line requirement is dosed HERE, in %. Leave it at 100 unless two feeders are splitting it."
                    value={split ? share : ''}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      patch(i, { share: Number.isFinite(v) ? v : 100 });
                    }}
                  />
                ) : (
                  <input
                    className="fe-kgh"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="kg/h"
                    title="kg/h of this material. This item is on no line, so there is no recipe to work it out from."
                    value={r.kgh ?? ''}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      patch(i, { kgh: Number.isFinite(v) && v > 0 ? v : undefined });
                    }}
                  />
                )}
                <button
                  className="fe-del"
                  title="Stop dosing this material here"
                  onClick={() => commit(rows.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
                {r.part && (
                  <div className="fe-meta">
                    <span className="fe-part">{r.part}</span>
                    {f ? (
                      <span
                        className="fe-derived"
                        title="Worked out from the recipe and the line rate"
                      >
                        {kgh > 0 ? formatRate(kgh) : 'no rate on the line yet'}
                      </span>
                    ) : (
                      EP_INDEX[r.part]?.category
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <button
            className="fe-add"
            title="Charge another of this line's materials in at this item"
            onClick={() => setRows([...rows, { part: '' }])}
          >
            ＋ Add material
          </button>
        </>
      )}
    </div>
  );
}
